import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { DateField } from '@/components/ui/date-time-field';
import { EmployeePicker } from './EmployeePicker';
import { useT } from '@/i18n/LanguageContext';

const addMonths = (iso, months) => {
  if (!iso || !months) return '';
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + Number(months));
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, last));
  return d.toISOString().slice(0, 10);
};

const INSPECTION_KEYS = {
  interval: 'reg.insp.interval', last: 'reg.insp.last', next: 'reg.insp.next',
  owner: 'reg.insp.owner', overdue: 'reg.insp.overdue',
};
const CALIBRATION_KEYS = {
  interval: 'reg.calib.interval', last: 'reg.calib.last', next: 'reg.calib.next',
  owner: 'reg.calib.owner', overdue: 'reg.calib.overdue',
};

/**
 * <InspectionFields> — 点検 / 校正（仕様 2.1）。
 *
 * 単位は **月** で統一（どの台帳でも同じ）。次回点検日は「前回 + 間隔」を
 * 自動で見せ、手入力があればそれを優先する。期限切れはその場でバッジを出す。
 * 値は親が持つ（この部品は保存しない）。
 */
export const InspectionFields = ({
  value, onChange, employees = [], testPrefix = 'inspection',
  showCalibration = true, title = null,
}) => {
  const { t } = useT();
  const patch = (p) => onChange({ ...value, ...p });
  const today = new Date().toISOString().slice(0, 10);
  const derived = (prefix) => addMonths(value?.[`last_${prefix}_on`], value?.[`${prefix}_interval_months`]);
  const due = (prefix) => value?.[`next_${prefix}_on`] || derived(prefix);
  const overdue = (prefix) => { const d = due(prefix); return !!d && d < today; };

  const block = (prefix, keys) => (
    <div className="space-y-3" data-testid={`${testPrefix}-${prefix}`}>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-semibold text-zinc-600">{t(keys.interval)}</Label>
          <Input type="number" min="0" value={value?.[`${prefix}_interval_months`] ?? ''}
            onChange={(e) => patch({ [`${prefix}_interval_months`]: e.target.value })}
            placeholder={t('reg.intervalPh')} className="h-10 mt-1"
            data-testid={`${testPrefix}-${prefix}-interval`} />
        </div>
        <div>
          <Label className="text-xs font-semibold text-zinc-600">{t(keys.last)}</Label>
          <DateField value={value?.[`last_${prefix}_on`] || ''}
            onChange={(v) => patch({ [`last_${prefix}_on`]: v })}
            className="mt-1" inputClassName="h-10" testId={`${testPrefix}-${prefix}-last`} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-semibold text-zinc-600">{t(keys.next)}</Label>
          <DateField value={value?.[`next_${prefix}_on`] || ''}
            onChange={(v) => patch({ [`next_${prefix}_on`]: v })}
            className="mt-1" inputClassName="h-10" testId={`${testPrefix}-${prefix}-next`} />
          <p className="mt-1 text-[11px] text-zinc-400">
            {t('reg.insp.nextHint')}{derived(prefix) ? `（${derived(prefix)}）` : ''}
          </p>
        </div>
        <div>
          <Label className="text-xs font-semibold text-zinc-600">{t(keys.owner)}</Label>
          <EmployeePicker value={value?.[`${prefix}_owner`] || ''} employees={employees}
            onChange={(v) => patch({ [`${prefix}_owner`]: v })}
            testId={`${testPrefix}-${prefix}-owner`} />
        </div>
      </div>
      {overdue(prefix) && (
        <p className="rounded bg-rose-50 px-2 py-1 text-[11px] font-semibold text-rose-700"
          data-testid={`${testPrefix}-${prefix}-overdue`}>
          {t(keys.overdue, { due: due(prefix) })}
        </p>
      )}
    </div>
  );

  return (
    <section className="rounded-lg border border-zinc-200 p-3 space-y-3" data-testid={`${testPrefix}-section`}>
      <h3 className="text-xs font-bold text-zinc-700">{title || t('reg.insp.title')}</h3>
      {block('inspection', INSPECTION_KEYS)}
      {showCalibration && (
        <>
          <label className="flex items-center gap-2 text-xs font-semibold text-zinc-600">
            <input type="checkbox" checked={!!value?.calibration_required}
              onChange={(e) => patch({ calibration_required: e.target.checked })}
              className="h-4 w-4 rounded border-zinc-300 accent-teal-600"
              data-testid={`${testPrefix}-calibration-required`} />
            {t('reg.calib.required')}
          </label>
          {value?.calibration_required && block('calibration', CALIBRATION_KEYS)}
        </>
      )}
    </section>
  );
};

export default InspectionFields;
